import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import PropTypes from "prop-types";
import config from "@config";
import { formatDateTime } from "@transformers/date";
import { toTitleCase } from "@transformers/response";
import {
  Button,
  Icons,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  GSGrid,
  SectionComponent,
} from "@components";
import { AddMedicalHistoryItemModal } from "@modules/employee/components";
import { connect } from "react-redux";
import { getIsAddMedicalHistoryItemModalVisible } from "@modules/employee/selectors";
import * as employeeSelectors from "@modules/employee/selectors";
import actionCreator from "@modules/employee/actionCreator";
import { toTableSort } from "@transformers/sort";

export class MedicalHistory extends React.Component {
  constructor(props) {
    super(props);
    this.removeMedicalHistoryItem = () =>
      this.props.removeMedicalHistoryItem({
        medicalHistoryItemId: this.props.medicalHistoryItemId,
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });

    this.onAddMedicalHistoryItem = ({ formData }) => {
      this.props.addMedicalHistoryItem({
        formData,
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    };

    this.onToggleRemoveMedicalHistoryItemModal = id => () =>
      this.props.toggleRemoveMedicalHistoryItemModal({
        medicalHistoryItemId: id,
        visible: true,
      });

    this.onSort = sort => {
      this.props.onSort({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        sort,
      });
    };
  }

  get columns() {
    return [
      {
        name: this.props.t("ID"),
        renderCell: ({ row }: { row: Object }) => item.id,
        key: "id",
        sortable: true,
      },
      {
        name: this.props.t("TYPE"),
        renderCell: ({ row }: { row: Object }) => toTitleCase(item.type),
        key: "type",
        sortable: true,
      },
      {
        name: this.props.t("ITEM"),
        renderCell: ({ row }: { row: Object }) =>
          item.type === "CONDITION" ? item.condition.name : item.treatment.name,
        key: "name",
        sortable: true,
      },
      {
        name: this.props.t("ADDITIONAL_NOTES"),
        renderCell: ({ row }: { row: Object }) => item.notes,
        key: "notes",
        sortable: true,
      },
      {
        name: this.props.t("DATE_RECORDED"),
        renderCell: ({ row }: { row: Object }) => formatDateTime(item.createdAt),
        key: "createdAt",
        sortable: true,
      },
      {
        name: this.props.t("RECORDED_BY"),
        renderCell: ({ row }: { row: Object }) =>
          `${item.updatedBy.firstName} ${item.updatedBy.lastName}`,
        key: "recordedBy",
      },
      {
        name: this.props.t("ASSOCIATED_ABSENCE"),
        renderCell: ({ row }: { row: Object }) =>
          item.absenceId || this.props.t("NOT_ASSOCIATED_ABSENCE"),
        key: "associatedAbsence",
      },
      {
        name: "",
        key: "actions",
        renderCell: ({ row }: { row: Object }) => {
          return (
            <span
              className="material-icons font-size-26 pointer"
              onClick={this.onToggleRemoveMedicalHistoryItemModal(row.id)}
            >
              delete
            </span>
          );
        },
      },
    ];
  }

  render() {
    return (
      <Fragment>
        <SectionComponent
          title={<h5>{this.props.t("MEDICAL_HISTORY")}</h5>}
          wrapperClassName="flex-column"
          rightLink={
            <Button materialIcon="add_box" onClick={this.props.onAddMedicalHistoryItemHandler}>
              {this.props.t("ADD")}
            </Button>
          }
        >
          <GSGrid
            className="pb-5"
            rows={this.props.medicalHistory}
            columns={this.columns}
            onSortChange={this.onSort}
            sort={toTableSort(this.props.sort)}
          />
          <Pagination
            className="my-3 d-flex justify-content-center"
            currentPage={this.props.pagination.pageNumber}
            totalPages={this.props.pagination.totalPages}
            onChange={this.props.onPageChange}
          />
        </SectionComponent>
        {this.props.schema && this.props.isAddMedicalHistoryItemModalVisible && (
          <AddMedicalHistoryItemModal
            onDismissHandler={this.props.onDismissAddMedicalHistoryItemHandler}
            onSubmit={this.onAddMedicalHistoryItem}
            schema={this.props.schema}
          />
        )}
        {this.props.isRemoveMedicalHistoryItemModalVisible && (
          <Modal className="modal-small">
            <ModalHeader toggle={this.props.onDismissRemoveMedicalHistoryItemHandler}>
              {this.props.t("ARE_YOU_SURE")}
            </ModalHeader>
            <ModalBody>
              {this.props.t("REMOVE_CONFIRMATION", {
                item: <strong>{this.props.medicalHistoryItemName},</strong>,
              })}
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                onClick={this.props.onDismissRemoveMedicalHistoryItemHandler}
                data-dismiss="modal"
              >
                {this.props.t("CANCEL")}
              </Button>
              <Button onClick={this.removeMedicalHistoryItem}>{this.props.t("REMOVE_ITEM")}</Button>
            </ModalFooter>
          </Modal>
        )}
      </Fragment>
    );
  }
}

MedicalHistory.propTypes = {
  t: PropTypes.func.isRequired,
  medicalHistory: PropTypes.array.isRequired,
  onAddMedicalHistoryItemHandler: PropTypes.func.isRequired,
  onDismissAddMedicalHistoryItemHandler: PropTypes.func.isRequired,
  isAddMedicalHistoryItemModalVisible: PropTypes.bool,
  schema: PropTypes.object,
  addMedicalHistoryItem: PropTypes.func.isRequired,
  employeeId: PropTypes.number.isRequired,
  clientId: PropTypes.number.isRequired,
  toggleRemoveMedicalHistoryItemModal: PropTypes.func.isRequired,
  isRemoveMedicalHistoryItemModalVisible: PropTypes.bool,
  onDismissRemoveMedicalHistoryItemHandler: PropTypes.func.isRequired,
  medicalHistoryItemId: PropTypes.number,
  medicalHistoryItemName: PropTypes.string,
  removeMedicalHistoryItem: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  pagination: PropTypes.shape({
    pageNumber: PropTypes.number,
    totalPages: PropTypes.number.isRequired,
  }),
  sort: PropTypes.array,
};

const mapStateToProps = state => {
  const medicalHistoryItemId = employeeSelectors.getMedicalHistoryItemId(state);

  return {
    isAddMedicalHistoryItemModalVisible: getIsAddMedicalHistoryItemModalVisible(state),
    schema: employeeSelectors.getMedicalHistorySchema(state),
    isRemoveMedicalHistoryItemModalVisible: employeeSelectors.getIsRemoveMedicalHistoryItemModalVisible(
      state,
    ),
    medicalHistoryItemId,
    medicalHistoryItemName: employeeSelectors.getMedicalHistoryItemName(medicalHistoryItemId)(
      state,
    ),
    pagination: employeeSelectors.getMedicalHistoryPagination(state),
    sort: employeeSelectors.getEmployeeMedicalHistorySort(state),
  };
};

const mapDispatchToProps = {
  onAddMedicalHistoryItemHandler: () => actionCreator.toggleAddMedicalHistoryItemModal(true),
  onDismissAddMedicalHistoryItemHandler: () =>
    actionCreator.toggleAddMedicalHistoryItemModal(false),
  addMedicalHistoryItem: actionCreator.addMedicalHistoryItem,
  toggleRemoveMedicalHistoryItemModal: actionCreator.toggleRemoveMedicalHistoryItemModal,
  onDismissRemoveMedicalHistoryItemHandler: () =>
    actionCreator.toggleRemoveMedicalHistoryItemModal({ visible: false }),
  removeMedicalHistoryItem: actionCreator.removeMedicalHistoryItem,
  onSort: actionCreator.setEmployeeMedicalHistorySort,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(MedicalHistory));
